import { Caption1, tokens } from "@fluentui/react-components";
import { Delete16Regular } from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogCheckbox, DialogField, DialogGrid2, DialogToggleField } from "@/components/app-dialog";
import { DateField } from "@/components/date-field";
import { FuiButton, FuiDropdown, FuiInput } from "@/components/fui-control";
import type { BillingCycle, CostOptions, ISODate, ProjectBudget, SolutionCategory, SolutionItem, UserResource } from "@/engine/types";
import { fmtCurrency } from "@/lib/format";
import { monthsBetween } from "./portfolio";
import { BILLING_CYCLE_OPTIONS, SOLUTION_CATEGORY_OPTIONS, SOLUTION_STATUS_OPTIONS } from "./resource-meta";

export function SolutionFormDialog({
  open,
  initial,
  resources,
  projectStart,
  projectFinish,
  masked,
  onClose,
  onSave,
  onDelete,
}: {
  open: boolean;
  initial?: SolutionItem | null;
  resources: UserResource[];
  projectStart: ISODate;
  projectFinish: ISODate;
  masked?: boolean;
  onClose: () => void;
  onSave: (sol: Omit<SolutionItem, "id">) => void;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [category, setCategory] = useState<SolutionCategory>(initial?.category ?? "software");
  const [vendor, setVendor] = useState(initial?.vendor ?? "");
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(initial?.billingCycle ?? "yearly");
  const [unitCost, setUnitCost] = useState(initial ? String(initial.unitCost) : "");
  const [quantity, setQuantity] = useState(String(initial?.quantity ?? 1));
  const [startDate, setStartDate] = useState<ISODate | undefined>(initial?.startDate ?? projectStart);
  const [endDate, setEndDate] = useState<ISODate | undefined>(initial?.endDate ?? (initial?.billingCycle === "once" ? undefined : projectFinish));
  const [assignedResourceId, setAssignedResourceId] = useState(initial?.assignedResourceId ?? "");
  const [status, setStatus] = useState<SolutionItem["status"]>(initial?.status ?? "contracted");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const u = Number(unitCost);
  const q = Number(quantity);
  const unitOk = unitCost.trim() !== "" && Number.isFinite(u) && u >= 0;
  const qtyOk = Number.isFinite(q) && q >= 1;
  const datesOk = !startDate || !endDate || startDate <= endDate;
  const valid = name.trim().length > 0 && vendor.trim().length > 0 && unitOk && qtyOk && datesOk;
  const total = unitOk && qtyOk ? u * q : 0;

  const spreadMonths = useMemo(() => {
    if (billingCycle === "once" || !startDate || !endDate || !datesOk) return 1;
    return Math.max(1, monthsBetween(startDate, endDate).length);
  }, [billingCycle, startDate, endDate, datesOk]);

  const qtyHint = useMemo(() => {
    if (billingCycle === "monthly" && startDate && endDate && datesOk) return `기간 기준 ${spreadMonths}개월`;
    if (billingCycle === "yearly") return "연 단위 계약 수";
    return "코어·계정·장비 수";
  }, [billingCycle, startDate, endDate, datesOk, spreadMonths]);

  const engineerOptions = useMemo(() => [{ value: "", label: "(미지정)" }, ...resources.map((r) => ({ value: r.id, label: `${r.displayName} · ${r.roleTitle}` }))], [resources]);

  const submit = () => {
    if (!valid) return;
    onSave({
      name: name.trim(),
      category,
      vendor: vendor.trim(),
      billingCycle,
      unitCost: u,
      quantity: q,
      totalCost: total,
      startDate,
      endDate: billingCycle === "once" ? undefined : endDate,
      assignedResourceId: assignedResourceId || undefined,
      status,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <>
    <AppDialog
      open={open}
      onClose={onClose}
      width="lg"
      title={initial ? "솔루션 상세" : "솔루션 · 라이선스 등록"}
      description="소프트웨어, 클라우드 인프라, SaaS, 보안·연계 라이선스 등 플랜에 도입되는 품목을 등록합니다."
      footerStart={
        <div style={{ display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS }}>
          {initial && onDelete && (
            <FuiButton density="dialog" appearance="secondary" icon={<Delete16Regular />} onClick={() => setConfirmDelete(true)}>
              삭제
            </FuiButton>
          )}
          {valid ? (
            <Caption1 style={{ fontVariantNumeric: "tabular-nums" }}>
              총 비용 <strong>{fmtCurrency(total, masked)}</strong>
              {billingCycle !== "once" && spreadMonths > 1 && total > 0 && (
                <span style={{ color: tokens.colorNeutralForeground3 }}>
                  {" "}· 월 약 {fmtCurrency(Math.round(total / spreadMonths), masked)} 배분 ({spreadMonths}개월)
                </span>
              )}
            </Caption1>
          ) : (
            <Caption1>{!name.trim() ? "솔루션 명칭" : !vendor.trim() ? "공급 벤더" : !unitOk ? "단가" : !qtyOk ? "수량" : "기간"} 입력이 필요합니다</Caption1>
          )}
        </div>
      }
      footer={<DialogActionsBar onCancel={onClose} onConfirm={submit} disabled={!valid} />}
    >
      <DialogField label="솔루션 명칭" required>
        <FuiInput density="dialog" value={name} autoFocus onChange={(_, d) => setName(d.value)} placeholder="예: Oracle DB Enterprise (Core 4)" />
      </DialogField>
      <DialogGrid2>
        <DialogField label="분류">
          <FuiDropdown density="dialog" value={category} options={SOLUTION_CATEGORY_OPTIONS} onValueChange={(v) => setCategory(v as SolutionCategory)} />
        </DialogField>
        <DialogField label="공급 벤더" required>
          <FuiInput density="dialog" value={vendor} onChange={(_, d) => setVendor(d.value)} placeholder="예: 한국오라클, AWS" />
        </DialogField>
      </DialogGrid2>
      <DialogGrid2>
        <DialogField label="과금 방식">
          <FuiDropdown
            density="dialog"
            value={billingCycle}
            options={BILLING_CYCLE_OPTIONS}
            onValueChange={(v) => {
              setBillingCycle(v as BillingCycle);
              if (v === "once") setEndDate(undefined);
              else if (!endDate) setEndDate(projectFinish);
            }}
          />
        </DialogField>
        <DialogField label="상태">
          <FuiDropdown density="dialog" value={status} options={SOLUTION_STATUS_OPTIONS} onValueChange={(v) => setStatus(v as SolutionItem["status"])} />
        </DialogField>
      </DialogGrid2>
      <DialogGrid2>
        <DialogField label="단가" required>
          <FuiInput density="dialog" type="number" step={10000} value={unitCost} onChange={(_, d) => setUnitCost(d.value)} contentBefore="₩" placeholder="0" />
        </DialogField>
        <DialogField label="수량" required hint={qtyHint}>
          <FuiInput density="dialog" type="number" min={1} value={quantity} onChange={(_, d) => setQuantity(d.value)} />
        </DialogField>
      </DialogGrid2>
      <DialogGrid2>
        <DialogField label={billingCycle === "once" ? "도입일" : "계약 시작"}>
          <DateField size="sm" value={startDate} onChange={setStartDate} clearable aria-label="시작일" />
        </DialogField>
        {billingCycle !== "once" ? (
          <DialogField label="계약 종료" hint={!datesOk ? "종료일이 시작일보다 앞설 수 없습니다" : undefined}>
            <DateField size="sm" value={endDate} onChange={setEndDate} clearable aria-label="종료일" />
          </DialogField>
        ) : (
          <div />
        )}
      </DialogGrid2>
      <DialogField label="전담 엔지니어">
        <FuiDropdown density="dialog" value={assignedResourceId} options={engineerOptions} onValueChange={setAssignedResourceId} />
      </DialogField>
      <DialogField label="비고 / 용도">
        <FuiInput density="dialog" value={notes} onChange={(_, d) => setNotes(d.value)} placeholder="도입 목적, 적용 WBS 단계" />
      </DialogField>
    </AppDialog>
    <ConfirmDialog
      stacked
      open={confirmDelete}
      title="솔루션 삭제"
      message="이 항목을 삭제할까요? 총 비용이 예산 소진에서 제외됩니다."
      onCancel={() => setConfirmDelete(false)}
      onConfirm={() => {
        setConfirmDelete(false);
        onDelete?.();
      }}
    />
    </>
  );
}

export function CostOptionsDialog({ open, options, onClose, onSave }: { open: boolean; options: CostOptions; onClose: () => void; onSave: (opts: CostOptions) => void }) {
  const [workingDays, setWorkingDays] = useState(String(options.workingDaysPerMonth));
  const [includeOverhead, setIncludeOverhead] = useState(options.includeOverhead);
  const [overheadPercent, setOverheadPercent] = useState(String(Math.round((options.overheadRatio - 1) * 100)));
  const [includeTech, setIncludeTech] = useState(options.includeTechnicalFee);
  const [techPercent, setTechPercent] = useState(String(Math.round(options.technicalFeeRatio * 100)));
  const [hideByDefault, setHideByDefault] = useState(options.hideRatesByDefault);

  const wd = Number(workingDays);
  const oh = Number(overheadPercent);
  const tf = Number(techPercent);
  const wdOk = workingDays.trim() !== "" && Number.isFinite(wd) && wd >= 1 && wd <= 31;
  const ohOk = overheadPercent.trim() !== "" && Number.isFinite(oh) && oh >= 0;
  const tfOk = techPercent.trim() !== "" && Number.isFinite(tf) && tf >= 0;
  const valid = wdOk && (!includeOverhead || ohOk) && (!includeTech || tfOk);
  const multiplier = (includeOverhead && ohOk ? 1 + oh / 100 : 1) * (1 + (includeTech && tfOk ? tf / 100 : 0));

  const submit = () => {
    if (!valid) return;
    onSave({
      workingDaysPerMonth: wd,
      includeOverhead,
      overheadRatio: ohOk ? 1 + oh / 100 : options.overheadRatio,
      includeTechnicalFee: includeTech,
      technicalFeeRatio: tfOk ? tf / 100 : options.technicalFeeRatio,
      hideRatesByDefault: hideByDefault,
    });
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="md"
      title="비용 산정 옵션"
      description="적용하면 모든 금액이 다시 계산됩니다."
      footerStart={
        valid ? (
          <Caption1 style={{ fontVariantNumeric: "tabular-nums" }}>인건비 합계 = 직접인건비 × {multiplier.toFixed(3).replace(/\.?0+$/, "")}</Caption1>
        ) : (
          <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>입력값을 확인하세요</Caption1>
        )
      }
      footer={<DialogActionsBar onCancel={onClose} onConfirm={submit} confirmLabel="옵션 적용" disabled={!valid} />}
    >
      <DialogField label="1 M/M 기준 근무일수" required hint={wdOk ? "SW 대가산정 표준 20.83일 또는 21일" : <span style={{ color: tokens.colorPaletteRedForeground1 }}>1~31 사이로 입력하세요</span>}>
        <FuiInput density="dialog" type="number" min={1} max={31} step={0.01} value={workingDays} onChange={(_, d) => setWorkingDays(d.value)} contentAfter="일" aria-label="1 M/M 기준 근무일수" aria-invalid={!wdOk} />
      </DialogField>
      <DialogGrid2>
        <DialogToggleField label="제경비(Overhead) 포함" checked={includeOverhead} onCheckedChange={setIncludeOverhead} hint="직접인건비 대비" error={includeOverhead && !ohOk ? "0 이상으로 입력하세요" : undefined}>
          <FuiInput density="dialog" type="number" min={0} value={overheadPercent} onChange={(_, d) => setOverheadPercent(d.value)} contentAfter="%" disabled={!includeOverhead} aria-label="제경비율" aria-invalid={includeOverhead && !ohOk} />
        </DialogToggleField>
        <DialogToggleField label="기술료(Technical fee) 포함" checked={includeTech} onCheckedChange={setIncludeTech} hint="(직접인건비 + 제경비) 대비" error={includeTech && !tfOk ? "0 이상으로 입력하세요" : undefined}>
          <FuiInput density="dialog" type="number" min={0} value={techPercent} onChange={(_, d) => setTechPercent(d.value)} contentAfter="%" disabled={!includeTech} aria-label="기술료율" aria-invalid={includeTech && !tfOk} />
        </DialogToggleField>
      </DialogGrid2>
      <DialogField label="공개 범위" hint="켜면 일반 팀원 화면에서 단가와 금액이 •••로 가려집니다">
        <DialogCheckbox label="일반 팀원에게 단가·금액 기본 비공개" checked={hideByDefault} onChange={setHideByDefault} />
      </DialogField>
    </AppDialog>
  );
}

export function BudgetEditDialog({
  open,
  budget,
  projected,
  masked,
  onClose,
  onSave,
}: {
  open: boolean;
  budget: ProjectBudget;
  projected?: { labor: number; solution: number; total: number };
  masked?: boolean;
  onClose: () => void;
  onSave: (patch: Partial<ProjectBudget>) => void;
}) {
  const [totalBudget, setTotalBudget] = useState(String(budget.totalBudget));
  const [labor, setLabor] = useState(String(budget.targetLaborCost));
  const [solution, setSolution] = useState(String(budget.targetSolutionCost));
  const [expense, setExpense] = useState(String(budget.targetExpenseCost));
  const [contingency, setContingency] = useState(String(budget.contingencyCost));

  const n = (s: string) => (s.trim() === "" ? NaN : Number(s));
  const vals = { totalBudget: n(totalBudget), labor: n(labor), solution: n(solution), expense: n(expense), contingency: n(contingency) };
  const allOk = Object.values(vals).every((v) => Number.isFinite(v) && v >= 0);
  const targetsSum = (vals.labor || 0) + (vals.solution || 0) + (vals.expense || 0) + (vals.contingency || 0);
  const gap = (vals.totalBudget || 0) - targetsSum;

  const submit = () => {
    if (!allOk) return;
    onSave({ totalBudget: vals.totalBudget, targetLaborCost: vals.labor, targetSolutionCost: vals.solution, targetExpenseCost: vals.expense, contingencyCost: vals.contingency });
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      width="md"
      title="예산안 변경"
      description="발주처 승인 총액과 내부 목표 원가(인건비·솔루션·직접경비·예비비)를 조정합니다."
      footerStart={
        allOk ? (
          <Caption1 style={{ color: gap < 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground3, fontVariantNumeric: "tabular-nums" }}>
            목표 합계 {fmtCurrency(targetsSum, masked)} · {gap >= 0 ? `미배정 ${fmtCurrency(gap, masked)}` : `총액 초과 ${fmtCurrency(-gap, masked)}`}
          </Caption1>
        ) : (
          <Caption1>모든 금액을 0 이상으로 입력하세요</Caption1>
        )
      }
      footer={<DialogActionsBar onCancel={onClose} onConfirm={submit} disabled={!allOk} />}
    >
      <DialogField label="총 승인 사업 예산" required hint={projected ? `현재 총 예상 소진 ${fmtCurrency(projected.total, masked)}` : undefined}>
        <FuiInput density="dialog" type="number" step={1000000} value={totalBudget} onChange={(_, d) => setTotalBudget(d.value)} contentBefore="₩" />
      </DialogField>
      <DialogGrid2>
        <DialogField label="목표 인건비" hint={projected ? `현재 예상 ${fmtCurrency(projected.labor, masked)} (제경비·기술료 포함)` : undefined}>
          <FuiInput density="dialog" type="number" step={1000000} value={labor} onChange={(_, d) => setLabor(d.value)} contentBefore="₩" />
        </DialogField>
        <DialogField label="목표 솔루션비" hint={projected ? `현재 등록 합계 ${fmtCurrency(projected.solution, masked)}` : undefined}>
          <FuiInput density="dialog" type="number" step={1000000} value={solution} onChange={(_, d) => setSolution(d.value)} contentBefore="₩" />
        </DialogField>
      </DialogGrid2>
      <DialogGrid2>
        <DialogField label="직접경비 (출장·교육 등)" hint="산정 모델에 목표액이 그대로 반영됩니다">
          <FuiInput density="dialog" type="number" step={1000000} value={expense} onChange={(_, d) => setExpense(d.value)} contentBefore="₩" />
        </DialogField>
        <DialogField label="예비비 (Contingency)" hint="리스크 대응을 위한 별도 보류 예산">
          <FuiInput density="dialog" type="number" step={1000000} value={contingency} onChange={(_, d) => setContingency(d.value)} contentBefore="₩" />
        </DialogField>
      </DialogGrid2>
    </AppDialog>
  );
}
